import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function Section({ number, title, children }) {
  return (
    <div className="flex space-x-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-400/10 border border-pink-400/30 flex items-center justify-center text-pink-400 text-sm font-medium">
        {number}
      </div>
      <div className="flex-1 pb-8">
        <h3 className="text-white font-medium mb-2">{title}</h3>
        <div className="text-gray-400 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

export default function NftsGuide() {
  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/guide" className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Guide
      </Link>
      <h1 className="text-3xl font-bold text-white mb-8">NFT Collections on Vextaris</h1>

      <p className="text-gray-400 leading-relaxed mb-10">
        Vextaris uses the VSC-21 standard for non-fungible tokens. Like VSC-7 for fungible tokens,
        NFT creation is a native protocol operation — no smart contracts required. Collections support
        royalties, metadata locking, and on-chain provenance tracking.
      </p>

      <div className="space-y-2">
        <Section number={1} title="Navigate to NFTs">
          <p>
            Go to the <Link to="/nfts" className="text-vextaris-blue hover:underline">NFTs</Link> page
            from the sidebar. Here you can browse existing collections or create a new one.
          </p>
        </Section>

        <Section number={2} title="Create a collection">
          <p>
            Click <span className="text-white">Create Collection</span> and fill in the required fields:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li><span className="text-white">Name</span> — the collection display name</li>
            <li><span className="text-white">Symbol</span> — short identifier (e.g. "SHGN")</li>
            <li><span className="text-white">Base URI</span> — the base URL for token metadata (e.g. https://myproject.com/metadata/)</li>
            <li><span className="text-white">Max Supply</span> — total number of NFTs that can be minted (0 for unlimited)</li>
            <li><span className="text-white">Royalties</span> — percentage of secondary sales paid to the creator (in basis points, max 1000 = 10%)</li>
          </ul>
        </Section>

        <Section number={3} title="Understand VSC-21">
          <p>
            VSC-21 is the Vextaris native NFT standard. It is conceptually similar to ERC-721 but
            implemented as a protocol-level primitive. Key differences from ERC-721:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li>No bytecode deployment — collection creation is a single native operation</li>
            <li>Royalties are enforced at the protocol level, not by marketplace convention</li>
            <li>Metadata can be permanently locked on-chain for immutable provenance</li>
            <li>Integrated with the reputation system and VexScan explorer</li>
          </ul>
        </Section>

        <Section number={4} title="Mint NFTs">
          <p>
            Once the collection exists, mint individual NFTs by specifying:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li><span className="text-white">Token ID</span> — unique identifier within the collection</li>
            <li><span className="text-white">Metadata URI</span> — URL pointing to the token's JSON metadata</li>
            <li><span className="text-white">Recipient</span> — the Vx address that receives the NFT (defaults to your address)</li>
          </ul>
          <p>
            The metadata URI typically resolves to a JSON file containing the NFT's name, description,
            image, and attributes array.
          </p>
        </Section>

        <Section number={5} title="Metadata structure">
          <p>
            Each NFT's metadata URI should point to a JSON file with this structure:
          </p>
          <div className="bg-black/30 rounded-lg p-3 font-mono text-xs text-gray-300 mt-1">
            <pre>{`{
  "name": "Shogun #1",
  "description": "First of the Shogun series",
  "image": "https://...",
  "attributes": [
    { "trait_type": "Rank", "value": "Daimyo" },
    { "trait_type": "Power", "value": 92 }
  ]
}`}</pre>
          </div>
          <p>
            The full metadata URL is formed by
            combining <span className="text-white">base_uri + token_id</span> (e.g.
            https://myproject.com/metadata/1). Store images and metadata on IPFS or Arweave for
            permanence.
          </p>
        </Section>

        <Section number={6} title="Lock metadata">
          <p>
            Locking metadata makes the collection's base URI and individual token URIs permanently
            immutable. This is a strong provenance signal — it guarantees that the art and attributes
            cannot be changed after minting.
          </p>
          <p>
            Locking is irreversible. Make sure all metadata and images are finalized and hosted on
            permanent storage before locking.
          </p>
        </Section>

        <Section number={7} title="Transfer and burn">
          <p>
            NFTs can be transferred to any Vx address. The owner (or an approved operator) can also
            burn NFTs, permanently removing them from the collection. Burns reduce the circulating
            supply but do not affect the max supply cap.
          </p>
        </Section>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-vextaris-darker border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-3">Tips</h3>
        <ul className="text-gray-400 text-sm space-y-2 list-disc list-inside">
          <li>Set royalties at creation time — they <span className="text-white">cannot be changed later</span>.</li>
          <li>Use <span className="text-white">IPFS or Arweave</span> for metadata and image hosting. Centralized URLs can go offline.</li>
          <li>The full metadata URL is <code className="text-vextaris-blue">base_uri + token_id</code>. Plan your URI structure before creating the collection.</li>
          <li>Maximum royalty is <span className="text-white">10% (1000 basis points)</span>.</li>
          <li>Lock metadata after all NFTs are minted and verified. Locking is permanent.</li>
        </ul>
      </div>

      {/* Docs link */}
      <div className="mt-6 text-sm text-gray-500">
        For the full NFT specification, see{' '}
        <a href="https://docs.vextaris.io/token-standards/overview#vsc-21-non-fungible-tokens-nfts" target="_blank" rel="noopener noreferrer" className="text-vextaris-blue hover:underline">
          docs.vextaris.io/token-standards/overview#vsc-21-non-fungible-tokens-nfts
        </a>.
      </div>
    </div>
  );
}
